import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, FolderOpen, Search } from "lucide-react";

export default function InventoryCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", status: "Active" });
  const [saving, setSaving] = useState(false);

  const fetchCategories = () => {
    setLoading(true);
    api.get("/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetchCategories(); }, []);

  const openCreate = () => { setEditId(null); setForm({ name: "", description: "", status: "Active" }); setFormOpen(true); };
  const openEdit = (cat) => { setEditId(cat.category_id); setForm({ name: cat.name, description: cat.description || "", status: cat.status || "Active" }); setFormOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Category name is required"); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/categories/${editId}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/categories", form);
        toast.success("Category created");
      }
      setFormOpen(false);
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/categories/${cat.category_id}`);
      toast.success("Category deleted");
      fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || "Cannot delete — products linked"); }
  };

  const filtered = categories.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.description || "").toLowerCase().includes(search.toLowerCase()));
  const totalProducts = categories.reduce((s, c) => s + (c.product_count || 0), 0);

  return (
    <div className="animate-fade-in" data-testid="inventory-categories-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#1F2937] tracking-tight">Inventory Categories</h1>
          <p className="text-sm text-gray-500 mt-1">{categories.length} categories &middot; {totalProducts} products</p>
        </div>
        <Button data-testid="add-category-btn" onClick={openCreate} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" /> New Category
        </Button>
      </div>

      <div className="mb-5">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input data-testid="category-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search categories..." className="pl-9 rounded-sm border-gray-300 h-10" />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-sm animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-10 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">{search ? "No categories match your search." : "No categories yet. Create your first category."}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((cat) => (
            <Card key={cat.category_id} data-testid={`category-${cat.category_id}`} className="border border-gray-200 rounded-sm shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-sm bg-[#E63946]/10 flex items-center justify-center"><Package className="w-5 h-5 text-[#E63946]" /></div>
                    <div>
                      <h3 className="font-heading font-semibold text-sm text-[#1F2937]">{cat.name}</h3>
                      <Badge className={`mt-0.5 rounded-full text-[9px] ${cat.status === "Active" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>{cat.status}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button data-testid={`edit-category-${cat.category_id}`} variant="ghost" size="sm" onClick={() => openEdit(cat)} className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600"><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button data-testid={`delete-category-${cat.category_id}`} variant="ghost" size="sm" onClick={() => handleDelete(cat)} className="h-7 w-7 p-0 text-gray-400 hover:text-[#E63946]"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                {cat.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{cat.description}</p>}
                <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
                  <Package className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-600"><strong>{cat.product_count || 0}</strong> product{(cat.product_count || 0) !== 1 ? "s" : ""}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md" data-testid="category-form-dialog">
          <DialogHeader><DialogTitle className="text-lg font-semibold">{editId ? "Edit Category" : "New Category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Name *</label>
              <Input data-testid="category-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Plumbing, Electrical..." className="rounded-sm border-gray-300 h-10" autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Description</label>
              <textarea data-testid="category-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description..."
                className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-20 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="category-status-select" className="rounded-sm border-gray-300 h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-category-btn" onClick={handleSave} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {saving ? "Saving..." : editId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
